/**
 * extracts author info from comment (unified user model)
 * @param {Object} comment - comment object with user (unified users table)
 * @returns {Object} - { name, email, role, isEndUser }
 */
export const getCommentAuthor = (comment) => {
  const user = comment?.user;

  if (user) {
    return {
      name: user.first_name
        ? `${user.first_name} ${user.last_name || ''}`.trim()
        : user.username || user.email,
      email: user.email,
      role: user.Role?.name,
      isEndUser: user.is_internal === false,
    };
  }

  return { name: null, email: null, role: null, isEndUser: false };
};

/**
 * Organizes comments by field using composite key (formId_subformId_fieldId)
 * @param {Array} comments - Array of comment objects
 * @returns {Object} - { [formId_subformId_fieldId]: [comments] }
 */
export const organizeCommentsByField = (comments) => {
  if (!Array.isArray(comments)) {
    return {};
  }

  return comments.reduce((acc, comment) => {
    const formId = comment.form_id || comment.formId;
    const subformId = comment.subform_id || comment.subForm_id;
    const fieldId = comment.field_id;

    if (!formId || !subformId || !fieldId) {
      return acc;
    }

    const key = `${formId}_${subformId}_${fieldId}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(comment);
    return acc;
  }, {});
};

/**
 * Gets comments for a specific field
 * @param {Array} comments - Array of comment objects
 * @param {number|string} formId - Form ID
 * @param {number|string} subformId - Subform ID
 * @param {string} fieldId - Field ID
 * @returns {Array} - Filtered and sorted comments for the field
 */
export const getCommentsForField = (comments, formId, subformId, fieldId) => {
  if (!Array.isArray(comments) || !formId || !subformId || !fieldId) {
    return [];
  }

  return comments
    .filter((comment) => {
      const commentFormId = comment.form_id || comment.formId;
      const commentSubformId = comment.subform_id || comment.subForm_id;
      return (
        String(commentFormId) === String(formId) &&
        String(commentSubformId) === String(subformId) &&
        comment.field_id === fieldId
      );
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

/**
 * Counts comments for a specific field
 * @param {Array} comments - Array of comment objects
 * @param {number|string} formId - Form ID
 * @param {number|string} subformId - Subform ID
 * @param {string} fieldId - Field ID
 * @returns {number} - Comment count for the field
 */
export const getCommentCountForField = (
  comments,
  formId,
  subformId,
  fieldId
) => {
  return getCommentsForField(comments, formId, subformId, fieldId).length;
};
